import { FormattedMessage } from 'react-intl';

import { Sport } from '@sorare/core/src/__generated__/globalTypes';
import { FullWidthContainer } from '@sorare/core/src/atoms/layout/FullWidthContainer';
import type { AnalyticTag } from '@sorare/core/src/components/search/InstantSearch/types';
import { NoIndex } from '@sorare/core/src/contexts/seo';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import { useTitleAndDescription } from '@sorare/core/src/hooks/useTitleAndDescription';
import { SaleType, createCardSorts } from '@sorare/core/src/lib/algolia';
import type { FiltersGroup } from '@sorare/core/src/lib/filters';
import { metadatas } from '@sorare/core/src/lib/seo/common';
import { useDefaultFilters } from '@sorare/marketplace/src/hooks/useDefaultFilters';
import { AdvancedCardSearch } from '@sorare/marketplace/src/searchCards/AdvancedCardSearch';
import { playerPageFilters as footballPlayerPageFilters } from '@sorare/marketplace/src/searchCards/football/cardFilters';
import { playerPageFilters as mlbPlayerPageFilters } from '@sorare/marketplace/src/searchCards/mlb/cardFilters';
import { playerPageFilters as nbaPlayerPageFilters } from '@sorare/marketplace/src/searchCards/nba/cardFilters';

import { useLayoutData } from '#src/pages/players/$slug/layout.tsx';

const cardSortsWithBestValue = createCardSorts({ withBestValue: true });

const sportAnalyticTag: Record<Sport, AnalyticTag> = {
  [Sport.FOOTBALL]: 'Football',
  [Sport.NBA]: 'NBA',
  [Sport.BASEBALL]: 'MLB',
};

const cardFilterBySport: Record<
  Sport,
  (withSeasonScore: boolean) => FiltersGroup[]
> = {
  [Sport.FOOTBALL]: () => footballPlayerPageFilters,
  [Sport.NBA]: () => nbaPlayerPageFilters,
  [Sport.BASEBALL]: mlbPlayerPageFilters,
};

const Search = () => {
  const { slug, player } = useLayoutData();
  const { sport: sportFromContext } = useSportContext();
  const { displayName } = player;
  const filters = useDefaultFilters({
    playerSlug: slug,
  });

  const sport = player.sport || sportFromContext;

  const cardFilters = cardFilterBySport[sport](false);

  useTitleAndDescription(
    metadatas.playerCard.title,
    metadatas.playerCard.description,
    { display_name: displayName },
    {
      img: player.avatarPictureUrl,
    }
  );

  return (
    <>
      <NoIndex />
      <h1 className="sr-only">
        <FormattedMessage
          defaultMessage="{display_name} Cards"
          values={{ display_name: displayName }}
        />
      </h1>
      <FullWidthContainer>
        <AdvancedCardSearch
          /* remount the component on player change as the underline components
        do not react properly (might be due to algolia cache) */
          key={player.slug}
          sorts={cardSortsWithBestValue}
          defaultSort="Best Value"
          analyticsTags={['Player', sportAnalyticTag[player.sport]]}
          defaultFilters={filters}
          cardFilters={cardFilters}
          stackableTypes={[SaleType.PRIMARY_OFFER]}
          hideStackedSwitch
          topic={{ type: 'player', label: displayName }}
          sport={sport}
        />
      </FullWidthContainer>
    </>
  );
};

export default Search;
